import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import * as api from "../lib/api";
import { wsClient } from "../lib/websocket";
import { effectivePermissions, canFeature, ROLE_KEYS } from "../lib/permissions";

const AppContext = createContext(null);

function formatTimerDisplay(totalSeconds) {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

export function AppProvider({ children }) {
    // Auth state: null = checking, false = not authenticated, object = authenticated user
    const [authUser, setAuthUser] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [permCatalog, setPermCatalog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Impersonation state
    const [impersonating, setImpersonating] = useState(false);
    const [realUser, setRealUser] = useState(null);

    // ===== GLOBAL TIMER STATE =====
    const [timerState, setTimerState] = useState({
        isRunning: false,
        ticketId: null,
        ticketNumber: null,
        startedAt: null,
        elapsedSeconds: 0,
    });
    const timerIntervalRef = useRef(null);

    // Tick every second when running
    useEffect(() => {
        if (timerState.isRunning && timerState.startedAt) {
            timerIntervalRef.current = setInterval(() => {
                const started = new Date(timerState.startedAt).getTime();
                const now = Date.now();
                setTimerState((prev) => ({
                    ...prev,
                    elapsedSeconds: Math.floor((now - started) / 1000),
                }));
            }, 1000);
        }
        return () => {
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        };
    }, [timerState.isRunning, timerState.startedAt]);

    const startTimer = (ticketId, ticketNumber) => {
        // Stop any existing timer first (don't save — just discard)
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        setTimerState({
            isRunning: true,
            ticketId,
            ticketNumber,
            startedAt: new Date().toISOString(),
            elapsedSeconds: 0,
        });
    };

    const stopTimer = async () => {
        if (!timerState.isRunning || !timerState.ticketId) return null;
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        const durationMinutes = Math.max(1, Math.round(timerState.elapsedSeconds / 60));
        const agentId = (impersonating ? realUser : currentUser)?.id;
        let entry = null;
        if (agentId) {
            try {
                entry = await api.createTimeEntry(timerState.ticketId, agentId, {
                    duration_minutes: durationMinutes,
                    note: `Chronomètre — ${formatTimerDisplay(timerState.elapsedSeconds)}`,
                    started_at: timerState.startedAt,
                });
            } catch (err) {
                console.error("Failed to save timer entry:", err);
            }
        }
        setTimerState({
            isRunning: false,
            ticketId: null,
            ticketNumber: null,
            startedAt: null,
            elapsedSeconds: 0,
        });
        return entry;
    };

    const cancelTimer = () => {
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        setTimerState({
            isRunning: false,
            ticketId: null,
            ticketNumber: null,
            startedAt: null,
            elapsedSeconds: 0,
        });
    };

    // Check auth on mount
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const user = await api.authMe();
                setAuthUser(user);
            } catch {
                setAuthUser(false);
            }
        };
        checkAuth();
    }, []);

    // Load app data once authenticated
    const loadInitialData = useCallback(async () => {
        if (!authUser || authUser === false) return;
        try {
            setLoading(true);
            const [usersData, departmentsData, catalog] = await Promise.all([
                api.getUsers(),
                api.getDepartments(),
                api.getPermissionCatalog(),
            ]);
            setUsers(usersData);
            setDepartments(departmentsData);
            setPermCatalog(catalog);

            // Find the matching user in users list by email, or use authUser directly
            const matched = usersData.find((u) => u.email === authUser.email);
            setCurrentUser(matched || authUser);
        } catch (err) {
            console.error("Failed to load initial data:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [authUser]);

    useEffect(() => {
        if (authUser && authUser !== false) {
            loadInitialData();
        } else {
            setLoading(false);
        }
    }, [authUser, loadInitialData]);

    const logout = async () => {
        try {
            await api.authLogout();
        } catch (err) { console.error("Logout error:", err); }
        wsClient.disconnect();
        setAuthUser(false);
        setCurrentUser(null);
        setImpersonating(false);
        setRealUser(null);
    };

    // 10B — WebSocket connection lifecycle
    useEffect(() => {
        if (currentUser?.id) {
            wsClient.connect(currentUser.id);
        }
        return () => wsClient.disconnect();
    }, [currentUser?.id]);

    const startImpersonation = (targetUser) => {
        setRealUser(currentUser);
        setCurrentUser(targetUser);
        setImpersonating(true);
    };

    const stopImpersonation = () => {
        if (realUser) {
            setCurrentUser(realUser);
        }
        setRealUser(null);
        setImpersonating(false);
    };

    const refreshUsers = async () => {
        const usersData = await api.getUsers();
        setUsers(usersData);
    };

    const refreshDepartments = async () => {
        const departmentsData = await api.getDepartments();
        setDepartments(departmentsData);
    };

    const refreshPermCatalog = async () => {
        const catalog = await api.getPermissionCatalog();
        setPermCatalog(catalog);
        return catalog;
    };

    // Droits effectifs d'un utilisateur (rôle + fonctionnalités attribuées)
    const userPermissions = (user) => effectivePermissions(user, permCatalog);

    const getUserById = (userId) => {
        return users.find((u) => u.id === userId);
    };

    const getDepartmentById = (deptId) => {
        return departments.find((d) => d.id === deptId);
    };

    // Check permission — ALWAYS uses currentUser (during impersonation = the observed user)
    // Accepte un rôle ("admin", "agent"...) ou une fonctionnalité ("inventory", "inventory:manage")
    const hasPermission = (permission) => {
        if (!currentUser) return false;
        const role = currentUser.role;
        if (!ROLE_KEYS.includes(permission)) {
            const [feature, level] = permission.split(":");
            return canFeature(userPermissions(currentUser), feature, level || "view");
        }
        switch (permission) {
            case "admin":
                return role === "admin";
            case "agent":
                return role === "admin" || role === "agent";
            case "coordinator":
                return role === "admin" || role === "agent" || role === "coordinator";
            case "approver":
                return role === "admin" || role === "agent" || role === "coordinator" || role === "approver";
            default:
                return true;
        }
    };

    // Team supervisor: departments where the current user is manager or assistant
    const supervisedDepartments = React.useMemo(() => {
        if (!currentUser || !departments.length) return [];
        const uid = currentUser.id;
        return departments.filter(
            (d) => d.manager_id === uid || (d.assistant_ids || []).includes(uid),
        );
    }, [currentUser, departments]);

    const isTeamSupervisor = supervisedDepartments.length > 0;
    const supervisedDeptIds = React.useMemo(
        () => supervisedDepartments.map((d) => d.id),
        [supervisedDepartments],
    );

    const value = {
        authUser,
        setAuthUser,
        currentUser,
        users,
        departments,
        loading,
        error,
        logout,
        refreshUsers,
        refreshDepartments,
        getUserById,
        getDepartmentById,
        hasPermission,
        permCatalog,
        refreshPermCatalog,
        userPermissions,
        reload: loadInitialData,
        // Impersonation
        impersonating,
        realUser,
        startImpersonation,
        stopImpersonation,
        // Timer
        timerState,
        startTimer,
        stopTimer,
        cancelTimer,
        // 10B — WebSocket
        wsClient,
        // Team supervision
        supervisedDepartments,
        supervisedDeptIds,
        isTeamSupervisor,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useApp must be used within an AppProvider");
    }
    return context;
}

export { formatTimerDisplay };
